// public/v3/src/lib/pcm-principal.js — single source of truth for "which one
// condition is this PCM enrollment for". CJS (Jest-loadable) so
// billing-generate.js (note gate), careplan-generate.js (template selection),
// and onboard.jsx (enrollment capture) all agree.
//
// PCM (99424/99426) manages exactly ONE complex chronic condition under a
// disease-specific care plan. The designation is made by the provider at
// enrollment (billing.pcmCondition, matched against condition text,
// case-insensitive).
//
// Precedence (codex review 2026-07-20): a PERSISTED designation always wins —
// when billing.pcmCondition is set, it must match a documented condition or
// the enrollment is UNRESOLVED (null → PCM billing gates off, staff re-picks).
// The "sole documented condition" fallback applies only to legacy enrollments
// with no persisted designation. Checking single-condition first would let an
// EHR sync that renames/removes the designated disease silently switch the
// enrollment to whatever one condition remains.

const PCM_BASE_CODES = new Set(['99424', '99426']);

function isPcmProgramCode(code) {
  return PCM_BASE_CODES.has(String(code || '').trim());
}

// Display/matching text for a condition entry, tolerating both the Elation
// object shape and bare-string entries (CSV/synthetic charts — same fallback
// chain as workflow.js conditionLabel).
function pcmConditionText(c) {
  if (typeof c === 'string') return c.trim();
  if (c && typeof c === 'object') {
    return String(c.description || c.label || c.name || c.code || c.conditionId || '').trim();
  }
  return '';
}

// The single condition a PCM enrollment manages, or null when unresolved.
function pcmPrincipalCondition(patient) {
  const conditions = Array.isArray(patient && patient.conditions) ? patient.conditions.filter(Boolean) : [];
  const named = String((patient && patient.billing && patient.billing.pcmCondition) || '').trim().toLowerCase();
  if (named) {
    return conditions.find(function(c) {
      return pcmConditionText(c).toLowerCase() === named;
    }) || null;
  }
  return conditions.length === 1 ? conditions[0] : null;
}

module.exports = { PCM_BASE_CODES, isPcmProgramCode, pcmConditionText, pcmPrincipalCondition };
